import { useState, useEffect } from "react";
import Button from "react-bootstrap/Button";
import Table from "react-bootstrap/Table";
import Image from "react-bootstrap/Image";
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { FaPenToSquare, FaRegTrashCan, FaPlus, FaTriangleExclamation } from "react-icons/fa6";

import { ObjectStatus } from "../components/ObjectStatus";
import { getDataHelper, postDataWithHeaderHelper, uploadFileHelper } from "../requests/PostRequests";
import Header from "../components/Header";
import NavBar from "../components/NavBar";
import { DeleteConfirmation } from "../components/DeleteConfirmation";

const defaultTrainerObject = {
  id: null,
  name: "",
  role: "",
  description: "",
  image_url: ""
};

const Team = ({ onTriggerMessage }) => {

  const [team, setTeam] = useState([]);
  let formValues = {};

  // Current object for editing
  const [currentTrainer, setCurrentTrainer] = useState(defaultTrainerObject);

  // For displaying the trainer add/edit form
  const [trainerFormShowFlag, setTrainerFormShowFlag] = useState(false);
  const trainerFormClose = () => setTrainerFormShowFlag(false);
  const trainerFormShow = () => setTrainerFormShowFlag(true);

  // For displaying the delete trainer confirmation
  const [deleteTrainerModalShowFlag, setDeleteTrainerModalShowFlag] = useState(false);
  const deleteTrainerModalClose = () => setDeleteTrainerModalShowFlag(false);
  const deleteTrainerModalShow = () => setDeleteTrainerModalShowFlag(true);

  async function getTeam() {
    const onSuccess = (data) => {
      let newTeam = [];
      const rows = data.result.rows;
      const length = rows.length;

      for (let i = 0; i < length; i++) {
        newTeam.push({
          id: parseInt(rows[i].id),
          name: rows[i].name,
          role: rows[i].role,
          description: rows[i].description,
          image_url: rows[i].image_url,
          image_local: false,
          status: ObjectStatus.Sync,
        });
      }

      setTeam(newTeam.sort((a, b) => a.id - b.id));
    };

    await getDataHelper("/v1/trainers", onTriggerMessage, onSuccess);
  }

  /**
   * Upload Trainer image to the server based on the input
   * @param {*} trainer
   */
  async function uploadTrainerImage(trainer) {
    const onSuccess = (data) => {
      let updatedTrainer = { ...trainer };
      updatedTrainer.status = ObjectStatus.Updated;
      updatedTrainer.image_url = data.result.rows[0].url;
      setCurrentTrainer(updatedTrainer);

      onTriggerMessage(
        "Success",
        "Successfully uploaded trainer image to the server",
      );
    };

    // Upload images
    await uploadFileHelper("/v1/trainers/upload",
      {
        file_type: 'images',
        category: 'team',
        owner: 'tmp',
        name: trainer.image_url,
        file: trainer.image_file
      }, onTriggerMessage, onSuccess);
  }

  async function postTrainer(trainer) {
    const onSuccess = (data) => {
      const rows = data.result.rows;
      const newTrainerId = rows[0].trainer_id;

      let updatedTrainer = { ...trainer };
      updatedTrainer.status = ObjectStatus.Sync;
      updatedTrainer.id = newTrainerId;
      updatedTrainer.image_url = rows[0].image_url;

      setCurrentTrainer(updatedTrainer);
      setTeam([...team, updatedTrainer]);

      onTriggerMessage(
        "Success",
        "Successfully uploaded trainer to the server",
      );

      trainerFormClose();
    };

    const submitObject = {
      name: trainer.name,
      role: trainer.role,
      description: trainer.description,
      image_url: trainer.image_url,
      image_local: trainer.image_local
    };

    // Upload trainer
    await postDataWithHeaderHelper("/v1/trainers/add", submitObject, onTriggerMessage, onSuccess);
  }

  async function postUpdateTrainer(trainer) {
    const onSuccess = (data) => {
      const result = data.result.rows[0];

      let updatedTrainer = { ...trainer };
      updatedTrainer.status = ObjectStatus.Sync;
      updatedTrainer.image_url = result.imageUrl;
      updatedTrainer.image_local = false;

      const currentIndex = team.findIndex((t) => t.id == trainer.id);
      team[currentIndex] = updatedTrainer;
      setCurrentTrainer({ ...updatedTrainer });
      setTeam([...team]);

      onTriggerMessage(
        "Success",
        "Successfully updated trainer details on the server",
      );
    };

    const submitObject = {
      id: trainer.id,
      name: trainer.name,
      role: trainer.role,
      description: trainer.description,
      image_url: trainer.image_url,
      image_local: trainer.image_local
    };

    // Update course
    await postDataWithHeaderHelper("/v1/trainers/update", submitObject, onTriggerMessage, onSuccess);
  }

  async function postDeleteCourse(id) {
    const onSuccess = (_) => {

      setTeam(team.filter((t) => t.id != id));
      setCurrentTrainer({ ...defaultTrainerObject });

      onTriggerMessage(
        "Success",
        "Successfully deleted trainer from the server",
      );

      deleteTrainerModalClose();
    };

    const submitObject = {
      id: id
    };

    // Delete trainer
    await postDataWithHeaderHelper("/v1/trainers/delete", submitObject, onTriggerMessage, onSuccess);
  }

  useEffect(() => {
    async function fetchData() {
      await getTeam();
    }

    fetchData();
  }, []);

  /*******************************************************************************************************
   *  Functions for CourseForms
   ******************************************************************************************************/

  /**
   * Convert current form input and apply it onto the currentCourse so
   * we will have the latest course information
   * @returns latest course
   */
  function getLatestTrainerBasedOnForm() {
    /// Convert and merge form values into currentCourse
    const conversionMap = {
      'id': 'id',
      'name': 'name',
      'role': 'role',
      'description': 'description',
      'imageUrl': 'image_url',
      'imageFile': 'image_file',
      'imageLocal': 'image_local',
    };

    let newTrainer = { ...currentTrainer };

    // We want to merge formValues with currentCourse so it does not get lost
    Object.entries(formValues).forEach(([key, value]) => {
      newTrainer[conversionMap[key]] = value;
    });

    return newTrainer;
  }

  function onChange(e) {
    formValues = { ...formValues, [e.target.name]: e.target.value };
  }

  function onImageUpload(filePath) {
    if (filePath == undefined) {
      onTriggerMessage(
        "File is not defined",
        `Select file is not defined, please try again. Accepted extensions are: ${acceptedExts}`,
        "danger"
      );
      return;
    }

    const filename = filePath[0].name;
    const ext = filename.split('.').pop();
    const acceptedExts = ["png", "jpeg"];

    // If the ext is not in the include list, we will not upload the file
    if (!acceptedExts.includes(ext)) {
      saveFormToCurrentResource();
      onTriggerMessage(
        "Invalid file extension",
        `We do not accept this file extension ${ext}, accepted formats: ${acceptedExts}`,
        "danger"
      );
      return;
    }

    let updatedTrainer = getLatestTrainerBasedOnForm();
    updatedTrainer.status = ObjectStatus.Updated;
    updatedTrainer.image_local = true;
    updatedTrainer.image_url = filename;
    updatedTrainer.image_file = filePath[0];

    // try to upload file to tmp location
    uploadTrainerImage(updatedTrainer);
  }

  /****************************************************************************
   Create, Update and Delete Functions
   ***************************************************************************/

  async function onAddSubmit() {
    let latestTrainer = getLatestTrainerBasedOnForm();

    // TODO
    // Validation here

    await postTrainer(latestTrainer);
  }

  async function onEditSubmit() {
    let latestTrainer = getLatestTrainerBasedOnForm();

    // TODO
    // Validation here

    await postUpdateTrainer(latestTrainer);
  }

  function onAddTrainer() {
    setCurrentTrainer({ ...defaultTrainerObject });
    trainerFormShow();
  }

  function onEditTrainer(id) {
    const tempObject = team.find(trainer => trainer.id == id);
    setCurrentTrainer({ ...tempObject });
    trainerFormShow();
  }

  function TrainerForm({ onCreate, onUpdate }) {
    const onSave = currentTrainer.id == null ? onCreate : onUpdate;

    return (
      <Modal size="lg" centered backdrop="static" keyboard={false} show={trainerFormShowFlag} onHide={trainerFormClose}>
        <Modal.Header>
          <Modal.Title>{currentTrainer.id == null ? "New Trainer" : "Edit Trainer"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group
              className="mb-3"
              controlId="trainerForm.name"
            >
              <Form.Label>Name</Form.Label>
              <Form.Control name="name" defaultValue={currentTrainer.name} placeholder="Trainer Name" onChange={onChange} />
            </Form.Group>
            <Form.Group
              className="mb-3"
              controlId="trainerForm.role"
            >
              <Form.Label>Role</Form.Label>
              <Form.Control
                name="role"
                defaultValue={currentTrainer.role}
                placeholder="Trainer's role (e.g. Trainer/Senior Trainer/Assistant)"
                onChange={onChange}
              />
            </Form.Group>
            <Form.Group
              className="mb-3"
              controlId="trainerForm.description"
            >
              <Form.Label>Description</Form.Label>
              <Form.Control
                name="description"
                defaultValue={currentTrainer.description}
                placeholder="Trainer's introduction..."
                as="textarea"
                rows={5}
                onChange={onChange}
              />
            </Form.Group>
            <Form.Group
              className="mb-3"
              controlId="trainerForm.image"
            >
              <Form.Label>Image</Form.Label><br />
              <Image src={currentTrainer.image_url} style={{ height: "5vw", width: "5vw" }} rounded />
            </Form.Group>
            <Form.Group
              className="mb-3"
              controlId="trainerForm.imageUrl"
            >
              <Form.Label>From the web:</Form.Label>
              <Form.Control
                name="imageUrl"
                defaultValue={currentTrainer.image_url}
                placeholder="http://localhost:3102/images/team/1/p1.png"
                onChange={onChange}
              />
            </Form.Group>
            <Form.Group
              className="mb-3"
              controlId="trainerForm.imageUpload"
            >
              <Form.Label>Local file upload:</Form.Label>
              <Form.Control name="imageFile" type="file" onChange={(e) => onImageUpload(e.target.files)} />
            </Form.Group>
          </Form>
        </Modal.Body >
        <Modal.Footer>
          <OverlayTrigger
            placement="left"
            overlay={
              <Tooltip>
                <FaTriangleExclamation fill="red" /> Discards all unsaved changes!
              </Tooltip>
            }
          >
            <Button variant="secondary" onClick={trainerFormClose}>
              Close
            </Button>
          </OverlayTrigger>
          <Button variant="primary" onClick={onSave}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal >
    );
  }

  function onDeleteTrainer(id) {
    const tempObject = team.find(trainer => trainer.id == id);
    setCurrentTrainer({ ...tempObject });
    deleteTrainerModalShow();
  }

  async function onDeleteTrainerConfirmation() {
    await postDeleteCourse(currentTrainer.id);
  }

  return (
    <>
      <NavBar />
      <div className="container-content-fixed">
        <div className="courses-hdr-grp">
          <Header title="Manage Team" />
          <Button className="btn-icon-grp" onClick={onAddTrainer} style={{ gap: "0.5em" }}>
            <FaPlus className="action-icons" /> Add New Trainer
          </Button>
        </div>
        <Table striped bordered hover>
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Role</th>
              <th className="w-50">Description</th>
              <th>Image</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {team.map(function (item, idx) {
              return (
                <tr key={idx}>
                  <td>{idx}</td>
                  <td>{item.name}</td>
                  <td>{item.role}</td>
                  <td>{item.description}</td>
                  <td>
                    <Image
                      className="mx-auto d-block border border-custom-secondary"
                      src={item.image_url}
                      roundedCircle
                      style={{
                        height: "5vw",
                        width: "5vw",
                      }}
                    />
                  </td>
                  <td>
                    <div className="action-btn-grp">
                      <Button variant="success" className="btn-icon-grp action-btn-icon-grp" onClick={() => onEditTrainer(item.id)}>
                        <FaPenToSquare className="action-icons" />Edit
                      </Button>
                      <Button variant="danger" className="btn-icon-grp action-btn-icon-grp" onClick={() => onDeleteTrainer(item.id)}>
                        <FaRegTrashCan className="action-icons" />Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        <TrainerForm
          onCreate={onAddSubmit}
          onUpdate={onEditSubmit}
        />
        <DeleteConfirmation
          title={currentTrainer.name}
          description="Are you sure you want to delete the following trainer?"
          showFlag={deleteTrainerModalShowFlag}
          onConfirmation={onDeleteTrainerConfirmation}
          onClose={deleteTrainerModalClose} />
      </div>
    </>
  );
};

export default Team;
